/**
 * Pure helpers for line-item index mapping. Keeps useInvoiceDetailFormState under 750 lines.
 */
import type { InvoicePart } from '../../types/invoice';

/** Stable expand/collapse key for matched NEX rows (must stay in sync with reconciliation UI). */
export function buildLineItemExpandKey(index: number, item: InvoicePart | undefined): string {
    return `${index}|${String(item?.partNumber ?? '').trim()}|${String(item?.title ?? '').trim()}|${item?.qty ?? ''}|${item?.totalBuyPrice ?? (item as { buyPrice?: number })?.buyPrice ?? ''}`;
}

/** Move one element from `from` to `to` (inclusive indices, same semantics as splice insert). */
export function arrayMove<T>(arr: readonly T[], from: number, to: number): T[] {
    if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return [...arr];
    const copy = [...arr];
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    return copy;
}

/**
 * After moving the row at `from` to index `to`, maps an old current-row index to its new index.
 */
export function remapCurrentLineIndexAfterReorder(from: number, to: number, i: number): number {
    if (i === from) return to;
    if (from < to) {
        if (i > from && i <= to) return i - 1;
        return i;
    }
    if (i >= to && i < from) return i + 1;
    return i;
}

/** Current-row index after deleting `deletedIndex`. The deleted row itself is dropped. */
export function remapCurrentIndexAfterDelete(deletedIndex: number, index: number): number | undefined {
    if (index === deletedIndex) return undefined;
    return index > deletedIndex ? index - 1 : index;
}

/** Saved-row indices after deleting the current row at `deletedIndex`. */
export function lineBackendMapAfterDelete(
    map: readonly (number | undefined)[],
    deletedIndex: number,
): (number | undefined)[] {
    return map.filter((_, index) => index !== deletedIndex);
}

/** Saved-row indices after inserting a new current row at `insertedIndex`. */
export function lineBackendMapAfterInsert(
    map: readonly (number | undefined)[],
    insertedIndex: number,
): (number | undefined)[] {
    const next = map.slice();
    next.splice(insertedIndex, 0, undefined);
    return next;
}

function lineItemIdentitySig(p: InvoicePart | undefined): string {
    return `${String(p?.partNumber ?? '').trim()}\x1e${String(p?.title ?? '').trim()}`;
}

function signatureCounts(sigs: string[]): Map<string, number> {
    const out = new Map<string, number>();
    for (const s of sigs) out.set(s, (out.get(s) ?? 0) + 1);
    return out;
}

/**
 * Maps each current edited line to the index of the same logical row in `backend`
 * (usually `invoice.lineItems` from the last save), using a longest-common-subsequence
 * alignment on (partNumber, title). This avoids greedy mis-pairing when duplicate
 * inserts share a part with unrelated rows — which previously skewed NEX remaps by
 * the number of inserted rows.
 */
export function computeLineItemBackendIndexByCurrentIndex(
    backend: InvoicePart[],
    currentLineItems: InvoicePart[]
): (number | undefined)[] {
    const B = backend.length;
    const C = currentLineItems.length;
    if (B === 0) return currentLineItems.map(() => undefined);
    if (C === 0) return [];

    const curSig = currentLineItems.map(lineItemIdentitySig);
    const backSig = backend.map(lineItemIdentitySig);

    /**
     * Fast path for pure reorder: same row count and same signature multiset.
     * In this case, every current row should still map to a backend row so
     * matched NEX lines remain attached while dragging rows up/down.
     */
    if (B === C) {
        const curCounts = signatureCounts(curSig);
        const backCounts = signatureCounts(backSig);
        const sameSignatures =
            curCounts.size === backCounts.size &&
            Array.from(curCounts.entries()).every(([sig, count]) => (backCounts.get(sig) ?? 0) === count);
        if (sameSignatures) {
            const backendIndicesBySig = new Map<string, number[]>();
            for (let bi = 0; bi < B; bi++) {
                const sig = backSig[bi];
                if (!backendIndicesBySig.has(sig)) backendIndicesBySig.set(sig, []);
                backendIndicesBySig.get(sig)!.push(bi);
            }
            return curSig.map((sig) => backendIndicesBySig.get(sig)?.shift());
        }
    }

    const dp: number[][] = Array.from({ length: C + 1 }, () => Array(B + 1).fill(0));
    for (let i = 1; i <= C; i++) {
        for (let j = 1; j <= B; j++) {
            if (curSig[i - 1] === backSig[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
        }
    }

    const result: (number | undefined)[] = Array(C).fill(undefined);
    let i = C;
    let j = B;
    while (i > 0 && j > 0) {
        if (curSig[i - 1] === backSig[j - 1] && dp[i][j] === dp[i - 1][j - 1] + 1) {
            result[i - 1] = j - 1;
            i -= 1;
            j -= 1;
        } else if (dp[i - 1][j] > dp[i][j - 1]) {
            i -= 1;
        } else if (dp[i - 1][j] < dp[i][j - 1]) {
            j -= 1;
        } else {
            i -= 1;
        }
    }

    return result;
}

export function computeLineItemOriginalByIndex(
    orig: { lineItems: InvoicePart[] },
    currentLineItems: InvoicePart[],
    syntheticEmpty: InvoicePart
): { origIndex: number | undefined; origItem: InvoicePart }[] {
    const backendIdxByCurrent = computeLineItemBackendIndexByCurrentIndex(orig.lineItems, currentLineItems);
    return currentLineItems.map((_, currentIdx) => {
        const oi = backendIdxByCurrent[currentIdx];
        if (oi == null) return { origIndex: undefined, origItem: syntheticEmpty };
        return { origIndex: oi, origItem: orig.lineItems[oi] };
    });
}
